/*
  Before/after comparison slider.

  Dragging is driven by pointer events rather than the range input.
  iOS Safari does not implement jump-to-position on a range track — the gesture
  only registers if it starts on the thumb — so the input is keyboard-only and
  pointer events handle touch and mouse on every platform.

  The slider lives inside a Flickity carousel, so the two gestures have to be
  separated by where the touch begins:

    - starts on the handle  -> compare drag, event withheld from Flickity
    - starts anywhere else  -> event passes through, carousel swipes normally

  There is no way to defer this decision until the finger moves: Flickity
  commits to a drag on pointerdown, so intent has to be resolved at that moment.
*/
(function () {
  'use strict';

  /* Half-width of the grab zone either side of the divider, in px.
     44 on touch matches Apple's minimum target size; mouse can be tighter. */
  var TOUCH_ZONE = 44;
  var MOUSE_ZONE = 28;

  var active = null;

  function currentPercent(el) {
    var input = el.querySelector('.ba-compare__range');
    return input ? parseFloat(input.value) : 50;
  }

  function isOnHandle(el, clientX, pointerType) {
    var rect = el.getBoundingClientRect();
    if (!rect.width) return false;

    var dividerX = rect.left + (rect.width * currentPercent(el)) / 100;
    var zone = pointerType === 'touch' ? TOUCH_ZONE : MOUSE_ZONE;

    return Math.abs(clientX - dividerX) <= zone;
  }

  function setPosition(el, clientX) {
    var rect = el.getBoundingClientRect();
    if (!rect.width) return;

    var pct = ((clientX - rect.left) / rect.width) * 100;
    if (pct < 0) pct = 0;
    if (pct > 100) pct = 100;

    el.style.setProperty('--ba-pos', pct + '%');

    var input = el.querySelector('.ba-compare__range');
    if (input) input.value = pct;
  }

  /*
    Capture phase is required, not stylistic.

    Flickity binds its drag handler to .flickity-viewport, an ancestor of
    .ba-compare. A bubble-phase listener on document runs after that handler,
    so stopPropagation there would arrive too late. Capturing at document
    intercepts on the way down — but only when the touch is on the handle.
  */
  document.addEventListener(
    'pointerdown',
    function (e) {
      if (!e.target || !e.target.closest) return;

      var el = e.target.closest('.ba-compare');
      if (!el) return;

      /* Not on the handle: leave the event alone so the carousel can swipe. */
      if (!isOnHandle(el, e.clientX, e.pointerType)) return;

      e.stopPropagation();
      e.preventDefault();

      active = el;

      if (el.setPointerCapture) {
        try {
          el.setPointerCapture(e.pointerId);
        } catch (err) {
          /* Safari throws if the pointer is already released. Harmless. */
        }
      }

      setPosition(el, e.clientX);
    },
    true
  );

  document.addEventListener('pointermove', function (e) {
    if (!active) return;
    setPosition(active, e.clientX);
  });

  function endDrag() {
    active = null;
  }

  document.addEventListener('pointerup', endDrag);
  /* Fires when a vertical swipe is claimed by page scroll (touch-action: pan-y). */
  document.addEventListener('pointercancel', endDrag);

  /* Cursor hint on desktop so the grab zone is discoverable. */
  document.addEventListener('mousemove', function (e) {
    if (active || !e.target || !e.target.closest) return;

    var el = e.target.closest('.ba-compare');
    if (!el) return;

    el.classList.toggle('ba-compare--grabbable', isOnHandle(el, e.clientX, 'mouse'));
  });

  /* Keyboard: arrow keys on the focused range input. */
  document.addEventListener('input', function (e) {
    if (!e.target || !e.target.classList) return;
    if (!e.target.classList.contains('ba-compare__range')) return;

    var el = e.target.closest('.ba-compare');
    if (el) el.style.setProperty('--ba-pos', e.target.value + '%');
  });
})();